import type { CollectibleInstance } from '../collectibles/CollectibleInstance';
import type { CollectibleManager } from '../collectibles/CollectibleManager';
import type { HazardInstance } from '../hazards/HazardInstance';
import type { HazardManager } from '../hazards/HazardManager';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';
import type { ObstacleManager } from '../obstacles/ObstacleManager';
import type { PlayerController } from '../player/PlayerController';
import { aabb, intersects, overlapsX, overlapsY, overlapsZ, type Aabb } from './Aabb';

/**
 * How the player entered an obstacle collider, derived from the previous
 * step: the axis that was still separated is the axis of entry.
 */
export const ContactKind = {
  /** Ran into it head-on (or stood up into it). */
  Front: 'FRONT',
  /** Clipped it during a lane change. */
  Side: 'SIDE',
  /** Came down onto it from above. */
  Top: 'TOP',
  /** Already overlapping in the previous step – contact was handled before. */
  Persistent: 'PERSISTENT',
} as const;
export type ContactKind = (typeof ContactKind)[keyof typeof ContactKind];

export interface ObstacleContact {
  readonly obstacle: ObstacleInstance;
  readonly kind: ContactKind;
}

export interface HazardContact {
  readonly hazard: HazardInstance;
  readonly kind: ContactKind;
}

export interface CollisionResult {
  readonly obstacles: ObstacleContact[];
  readonly collectibles: CollectibleInstance[];
  /**
   * With a magnet: untouched collectibles within its reach along the track – all lanes, ascending z.
   * CollectibleMagnet decides which of them it pulls in.
   */
  readonly nearby: CollectibleInstance[];
  /** The runner's collider of this step. */
  readonly runner: Readonly<Aabb>;
  /** Moving hazards (crane loads, excavator, vehicles). */
  readonly hazards: HazardContact[];
}

/** Broad-phase margin around the player along the track (meters). */
const QUERY_MARGIN = 4;

const byTrackZ = (a: CollectibleInstance, b: CollectibleInstance): number => a.trackCollider.minZ - b.trackCollider.minZ;

/** Track-space collision detection between the player and spawned entities. */
export class CollisionSystem {
  private readonly now = aabb();
  private readonly before = aabb();
  private readonly relative = aabb();
  private readonly obstacleScratch: ObstacleInstance[] = [];
  private readonly collectibleScratch: CollectibleInstance[] = [];
  private readonly hazardScratch: HazardInstance[] = [];
  private readonly result: CollisionResult = { obstacles: [], collectibles: [], nearby: [], hazards: [], runner: this.now };

  constructor(
    private readonly obstacles: ObstacleManager,
    private readonly collectibles: CollectibleManager,
    private readonly hazards: HazardManager | null = null,
  ) {}

  /** `magnetReach` > 0: untouched collectibles this far along the track are reported as `nearby`. */
  detect(player: Pick<PlayerController, 'colliderOf'>, magnetReach = 0): CollisionResult {
    const now = player.colliderOf('current', this.now);
    const before = player.colliderOf('previous', this.before);
    this.result.obstacles.length = 0;
    this.result.collectibles.length = 0;
    this.result.nearby.length = 0;
    this.result.hazards.length = 0;

    for (const obstacle of this.obstacles.query(now.minZ - QUERY_MARGIN, now.maxZ + QUERY_MARGIN, this.obstacleScratch)) {
      if (!intersects(now, obstacle.bounds)) continue;
      let kind: ContactKind | null = null;
      for (const collider of obstacle.trackColliders) {
        if (!intersects(now, collider)) continue;
        const k = classifyContact(before, now, collider);
        // A fresh contact on any collider wins over a persistent one.
        if (kind === null || kind === ContactKind.Persistent) kind = k;
      }
      if (kind) this.result.obstacles.push({ obstacle, kind });
    }

    const reach = Math.max(QUERY_MARGIN, magnetReach);
    for (const item of this.collectibles.query(now.minZ - reach, now.maxZ + reach, this.collectibleScratch)) {
      if (intersects(now, item.trackCollider)) this.result.collectibles.push(item);
      else if (magnetReach > 0 && item.trackCollider.maxZ > now.minZ - magnetReach && item.trackCollider.minZ < now.maxZ + magnetReach) {
        this.result.nearby.push(item);
      }
    }
    // Front to back: a magnet meets the bottles of a row in the order the runner reaches them.
    this.result.nearby.sort(byTrackZ);

    for (const hazard of this.hazards?.query(now.minZ - QUERY_MARGIN, now.maxZ + QUERY_MARGIN, this.hazardScratch) ?? []) {
      if (!intersects(now, hazard.bounds)) continue;
      let kind: ContactKind | null = null;
      for (let i = 0; i < hazard.trackColliders.length; i++) {
        const collider = hazard.trackColliders[i]!;
        if (!hazard.enabled[i] || !intersects(now, collider)) continue;
        // A collider that just appeared (rubble heap) is treated as standing still.
        const previous = hazard.previousEnabled[i] ? hazard.previousColliders[i]! : collider;
        const k = classifyMovingContact(before, now, previous, collider, this.relative);
        if (kind === null || kind === ContactKind.Persistent) kind = k;
      }
      if (kind) this.result.hazards.push({ hazard, kind });
    }
    return this.result;
  }
}

/**
 * Contact with a collider that moved during the step: classified in the collider's frame (the
 * player's previous box shifted by the collider's motion). A side contact the collider caused
 * itself (a swinging load or a steering vehicle ramming the runner) counts as frontal – only a
 * runner changing lanes into something gets away with a stumble.
 */
export function classifyMovingContact(
  before: Readonly<Aabb>,
  now: Readonly<Aabb>,
  colliderBefore: Readonly<Aabb>,
  colliderNow: Readonly<Aabb>,
  scratch: Aabb = aabb(),
): ContactKind {
  const dx = (colliderNow.minX + colliderNow.maxX - colliderBefore.minX - colliderBefore.maxX) / 2;
  const dy = (colliderNow.minY + colliderNow.maxY - colliderBefore.minY - colliderBefore.maxY) / 2;
  const dz = (colliderNow.minZ + colliderNow.maxZ - colliderBefore.minZ - colliderBefore.maxZ) / 2;
  scratch.minX = before.minX + dx;
  scratch.maxX = before.maxX + dx;
  scratch.minY = before.minY + dy;
  scratch.maxY = before.maxY + dy;
  scratch.minZ = before.minZ + dz;
  scratch.maxZ = before.maxZ + dz;
  const kind = classifyContact(scratch, now, colliderNow);
  if (kind !== ContactKind.Side) return kind;
  const playerDx = (now.minX + now.maxX - before.minX - before.maxX) / 2;
  return Math.abs(dx) > Math.abs(playerDx) ? ContactKind.Front : ContactKind.Side;
}

/**
 * Entry axis from a swept test: for every axis that was still separated in the
 * previous step, compute when during the step the overlap began – the axis that
 * closed LAST is the one the player came through. (A diagonal corner contact
 * during a lane change is a side contact, not a frontal crash.)
 */
export function classifyContact(before: Readonly<Aabb>, now: Readonly<Aabb>, collider: Readonly<Aabb>): ContactKind {
  const tx = overlapsX(before, collider) ? -Infinity : entryTime(before.minX, before.maxX, now.minX, now.maxX, collider.minX, collider.maxX);
  const ty = overlapsY(before, collider) ? -Infinity : entryTime(before.minY, before.maxY, now.minY, now.maxY, collider.minY, collider.maxY);
  const tz = overlapsZ(before, collider) ? -Infinity : entryTime(before.minZ, before.maxZ, now.minZ, now.maxZ, collider.minZ, collider.maxZ);
  if (tx === -Infinity && ty === -Infinity && tz === -Infinity) return ContactKind.Persistent;
  if (tz >= tx && tz >= ty) return ContactKind.Front;
  if (tx >= ty) return ContactKind.Side;
  return before.minY >= collider.maxY ? ContactKind.Top : ContactKind.Front;
}

/** Fraction of the step (0..1) at which a moving interval starts to overlap [cMin, cMax]. */
function entryTime(bMin: number, bMax: number, nMin: number, nMax: number, cMin: number, cMax: number): number {
  if (bMax <= cMin) {
    const travel = nMax - bMax;
    return travel > 0 ? (cMin - bMax) / travel : 0;
  }
  const travel = bMin - nMin;
  return travel > 0 ? (bMin - cMax) / travel : 0;
}
